/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import ReactSelect, {
  GroupedOptionsType,
  OptionsType,
  OptionTypeBase,
  ValueType,
} from 'react-select';

import CreatableSelect from 'react-select/creatable';

import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';

import DoubleArrowRight from 'assets/svg/double-arrow-right.svg';

import { Container, ButtonLink, SelectContainer } from './styles';

interface IPaginationProps {
  count: number;
  defaultNumberOfRows: number | string;
  onChangeRowsPerPage?: (number: number) => void;
  rowsPerPage: number;
  maxRowsPerPage?: number;
  enableCustomRowsPerPage?: boolean;
  options?:
    | GroupedOptionsType<OptionTypeBase>
    | OptionsType<OptionTypeBase>
    | undefined;
  page: number;
  onChangePage: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    page: number
  ) => void;
}

const PaginationComponent: React.FC<IPaginationProps> = ({
  count,
  onChangePage,
  rowsPerPage,
  onChangeRowsPerPage,
  options,
  defaultNumberOfRows,
  page,
  maxRowsPerPage = 500,
  enableCustomRowsPerPage = false,
}) => {
  const [pages, setPages] = useState<Array<number>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSelectFocused, setIsSelectFocused] = useState(false);

  const rowsPerPageOptions = useMemo(
    () =>
      options || [
        {
          label: 3,
          value: 3,
        },
        {
          label: 5,
          value: 5,
        },
        {
          label: 10,
          value: 10,
        },
        {
          label: 50,
          value: 50,
        },
        {
          label: 100,
          value: 100,
        },
      ],
    [options]
  );

  const selectedValue = useMemo(
    () => ({
      label: String(rowsPerPage || defaultNumberOfRows),
      value: Number(rowsPerPage || defaultNumberOfRows),
    }),
    [rowsPerPage, defaultNumberOfRows]
  );

  const normalizeRowsPerPage = useCallback(
    (value: number | string | undefined) => {
      const numberValue = Number(value);

      if (!numberValue || Number.isNaN(numberValue) || numberValue < 1) {
        return 1;
      }

      if (numberValue > maxRowsPerPage) {
        return maxRowsPerPage;
      }

      return numberValue;
    },
    [maxRowsPerPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (value: number | string | undefined) => {
      const newRowsPerPage = enableCustomRowsPerPage
        ? normalizeRowsPerPage(value)
        : Number(value);

      setInputValue('');
      setIsSelectFocused(false);

      if (onChangeRowsPerPage) {
        onChangeRowsPerPage(newRowsPerPage);
      }

      onChangePage(null, 0);
    },
    [
      enableCustomRowsPerPage,
      normalizeRowsPerPage,
      onChangeRowsPerPage,
      onChangePage,
    ]
  );

  const calcPagesRange = useCallback(
    inheritedPage => {
      const numberOfPages = Math.ceil(count / rowsPerPage);
      const numberOfItems = numberOfPages <= 5 ? numberOfPages : 5;
      const newPivotPage = inheritedPage >= 0 ? inheritedPage + 1 : 1;

      const limit =
        numberOfPages - newPivotPage < 2
          ? numberOfItems / 2 + 2 + newPivotPage - numberOfPages
          : numberOfItems / 2;

      const sum = Math.floor(
        1 +
          (newPivotPage > 3 && newPivotPage > numberOfItems - 2
            ? newPivotPage - limit
            : 0)
      );

      return [...Array(numberOfItems).keys()].map(key => key + sum);
    },
    [count, rowsPerPage]
  );

  const skip = useCallback(
    (event, inheritedPage) => {
      const numberOfPages = Math.ceil(count / rowsPerPage);

      let newPage = inheritedPage;

      if (newPage > numberOfPages - 1) {
        newPage = numberOfPages - 1;
      }

      if (newPage < 0) {
        newPage = 0;
      }

      onChangePage(event, newPage);
      setPages(calcPagesRange(newPage));
    },
    [calcPagesRange, onChangePage, count, rowsPerPage]
  );

  useEffect(() => {
    const calcPages = calcPagesRange(page);
    setPages(calcPages);
  }, [calcPagesRange, page]);

  const customStyles = {
    container: (provided: object) => ({
      ...provided,
      minWidth: '90px',
    }),
    control: (provided: object) => ({
      ...provided,
      minHeight: '36px',
    }),
    valueContainer: (provided: object) => ({
      ...provided,
      padding: '0 8px',
    }),
    menu: (provided: object) => ({
      ...provided,
      border: '1px solid #606060',
      borderRadius: '5px',
      overflow: 'hidden',
      boxShadow: 'none',
    }),
    menuList: (provided: object) => ({
      ...provided,
      padding: 0,
    }),
    input: (provided: object) => ({
      ...provided,
      color: '#606060',
    }),
    singleValue: (provided: object) => ({
      ...provided,
      color: '#606060',
      fontSize: '13px',
    }),
    placeholder: (provided: object) => ({
      ...provided,
      fontSize: '13px',
      lineHeight: '45px',
      fontWeight: 400,
      color: '#b5b5c3',
    }),
  };

  const totalPages = Math.ceil(count / rowsPerPage);

  const firstRegister = count === 0 ? 0 : page * rowsPerPage + 1;

  const lastRegister = Math.min((page + 1) * rowsPerPage, count);

  return (
    <Container>
      <div className="pagination">
        <button
          type="button"
          className="double-arrow arrow"
          onClick={event => skip(event, page - 5)}
          disabled={page <= 0}
        >
          <img src={DoubleArrowRight} alt="" />
        </button>

        <button
          type="button"
          className="arrow"
          onClick={event => skip(event, page - 1)}
          disabled={page <= 0}
        >
          <MdKeyboardArrowLeft size={20} color="#7E8299" />
        </button>

        <div className="links">
          {pages.map(page_ => (
            <ButtonLink
              onClick={event => skip(event, page_ - 1)}
              key={page_}
              type="button"
              active={page + 1 === page_}
            >
              {page_}
            </ButtonLink>
          ))}
        </div>

        <button
          type="button"
          disabled={page + 1 >= totalPages}
          className="arrow"
          onClick={event => skip(event, page + 1)}
        >
          <MdKeyboardArrowRight size={20} color="#7E8299" />
        </button>

        <button
          disabled={page + 1 >= totalPages}
          type="button"
          className="double-arrow arrow"
          onClick={event => skip(event, page + 5)}
        >
          <img src={DoubleArrowRight} alt="" />
        </button>
      </div>

      <div className="pages">
        {enableCustomRowsPerPage && (
          <div className="text">
            <p>Qtde. por pagina:</p>
          </div>
        )}
        <div className="select">
          <SelectContainer>
            {enableCustomRowsPerPage ? (
              <>
                {/* @ts-ignore */}
                <CreatableSelect
                  classNamePrefix="sel"
                  name="quantity"
                  styles={customStyles}
                  placeholder={isSelectFocused ? String(rowsPerPage) : ''}
                  isSearchable
                  menuPlacement="top"
                  value={inputValue || isSelectFocused ? null : selectedValue}
                  inputValue={inputValue}
                  controlShouldRenderValue={!inputValue && !isSelectFocused}
                  onFocus={() => {
                    setIsSelectFocused(true);
                  }}
                  onBlur={() => {
                    setIsSelectFocused(false);
                    setInputValue('');
                  }}
                  onInputChange={(newValue, actionMeta) => {
                    if (actionMeta.action === 'input-change') {
                      const onlyNumbers = newValue.replace(/\D/g, '');

                      if (onlyNumbers === '') {
                        setInputValue('');
                        return '';
                      }

                      if (Number(onlyNumbers) > maxRowsPerPage) {
                        setInputValue(String(maxRowsPerPage));
                        return String(maxRowsPerPage);
                      }

                      setInputValue(onlyNumbers);
                      return onlyNumbers;
                    }

                    return newValue;
                  }}
                  onChange={(value: ValueType<OptionTypeBase, false>) => {
                    handleChangeRowsPerPage(value?.value);
                  }}
                  onCreateOption={createdValue => {
                    handleChangeRowsPerPage(createdValue);
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' && inputValue) {
                      event.preventDefault();
                      handleChangeRowsPerPage(inputValue);
                    }
                  }}
                  isValidNewOption={createdValue => {
                    const numberValue = Number(createdValue);

                    return (
                      !!createdValue &&
                      !Number.isNaN(numberValue) &&
                      numberValue >= 1 &&
                      numberValue <= maxRowsPerPage
                    );
                  }}
                  formatCreateLabel={createdValue => `Usar ${createdValue}`}
                  options={rowsPerPageOptions}
                />
              </>
            ) : (
              // @ts-ignore
              <ReactSelect
                classNamePrefix="sel"
                name="quantity"
                menuPlacement="top"
                styles={customStyles}
                placeholder=""
                defaultValue={{
                  label: defaultNumberOfRows,
                  value: defaultNumberOfRows,
                }}
                onChange={(value: ValueType<OptionTypeBase, false>) => {
                  if (onChangeRowsPerPage) {
                    onChangeRowsPerPage(Number(value?.value));
                  }

                  onChangePage(null, 0);
                }}
                options={rowsPerPageOptions}
              />
            )}
          </SelectContainer>
        </div>

        <div className="text">
          <p>{`Registros ${firstRegister} a ${lastRegister} total ${count}`}</p>
        </div>
      </div>
    </Container>
  );
};

export default PaginationComponent;
